import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Cpu, Upload, Scan, ArrowRight, CircuitBoard, Zap, AlertTriangle, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import UploadZone from "@/components/UploadZone";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function HomePage() {
  const navigate = useNavigate();
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [ctNumber, setCtNumber] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [ctWarning, setCtWarning] = useState(null);
  const [isCheckingCt, setIsCheckingCt] = useState(false);
  const [detailedMode, setDetailedMode] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState("");

  // Check CT number when it changes
  const checkCtNumber = useCallback(async (ct) => {
    if (!ct || ct.length < 2) {
      setCtWarning(null);
      return;
    }
    
    setIsCheckingCt(true);
    try {
      const response = await axios.get(`${API}/ct-check/${encodeURIComponent(ct)}`);
      if (response.data.exists) {
        setCtWarning({
          message: response.data.message,
          analysisCount: response.data.analysis_count,
          previousResult: response.data.previous_result,
          previousDate: response.data.previous_date?.slice(0, 10) || "Unknown date"
        });
      } else {
        setCtWarning(null);
      }
    } catch (error) {
      console.error("CT check error:", error);
      setCtWarning(null);
    } finally {
      setIsCheckingCt(false);
    }
  }, []);

  const handleCtNumberChange = (e) => {
    const value = e.target.value;
    setCtNumber(value);
    // Debounce the check
    clearTimeout(window.ctCheckTimeout);
    window.ctCheckTimeout = setTimeout(() => checkCtNumber(value.trim()), 500);
  };

  const handleImageUpload = useCallback((file, side) => {
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPEG, PNG, or WEBP images.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result;
      if (side === "front") {
        setFrontImage(base64);
        setFrontPreview(base64);
      } else {
        setBackImage(base64);
        setBackPreview(base64);
      }
      toast.success(`${side.charAt(0).toUpperCase() + side.slice(1)} image uploaded`);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleAnalyze = async () => {
    if (!frontImage || !backImage) {
      toast.error("Please upload both front and back images");
      return;
    }

    if (!ctNumber.trim()) {
      toast.error("Please enter the CT number");
      return;
    }

    setIsAnalyzing(true);
    setAnalysisProgress("Uploading images...");
    toast.info(`Starting ${detailedMode ? 'detailed' : 'fast'} scan analysis...`, {
      duration: 5000,
    });
    
    try {
      const response = await axios.post(`${API}/analyze`, {
        front_image: frontImage,
        back_image: backImage,
        ct_number: ctNumber.trim(),
        detailed_mode: detailedMode,
      }, { timeout: detailedMode ? 180000 : 120000 });

      navigate("/analysis", {
        state: {
          result: response.data,
          frontImage: frontPreview,
          backImage: backPreview,
          ctNumber: ctNumber.trim(),
          gridSize: detailedMode ? 3 : 2,
        },
      });
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error(error.response?.data?.detail || "Analysis failed. Please try again.");
    } finally {
      setIsAnalyzing(false);
      setAnalysisProgress("");
    }
  };

  const clearImages = () => {
    setFrontImage(null);
    setBackImage(null);
    setFrontPreview(null);
    setBackPreview(null);
    setCtNumber("");
    setCtWarning(null);
    toast.info("Cleared");
  };

  return (
    <div className="min-h-screen bg-[#09090B] relative overflow-hidden">
      {/* Grid Background */}
      <div className="absolute inset-0 grid-bg opacity-40" />
      
      {/* Content */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-6 py-12">
        {/* Header */}
        <motion.header 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-between mb-16"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-cyan-500/10 border border-cyan-500/20 rounded-sm">
              <CircuitBoard className="w-8 h-8 text-cyan-400" />
            </div>
            <div>
              <h1 className="font-mono font-bold text-xl tracking-tight text-zinc-50">
                CORROSION<span className="text-cyan-400">AI</span>
              </h1>
              <p className="text-xs text-zinc-500 uppercase tracking-widest">
                Precision Detection System
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <a href="/history" className="flex items-center gap-2 text-zinc-400 hover:text-white font-mono text-xs uppercase tracking-wider">
              <History className="w-4 h-4"/>History
            </a>
            <div className="flex items-center gap-2 text-zinc-500">
              <Zap className="w-4 h-4 text-cyan-400" />
              <span className="text-xs font-mono uppercase tracking-wider">Powered by Claude Vision</span>
            </div>
          </div>
        </motion.header>

        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-center mb-16"
        >
          <h2 className="font-mono font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight text-zinc-50 uppercase mb-4">
            Precision Corrosion
            <br />
            <span className="text-cyan-400">Detection</span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            AI-powered motherboard inspection to identify corrosion and determine 
            pre-use condition with high accuracy analysis.
          </p>
        </motion.div>

        {/* CT Number Input */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="max-w-md mx-auto mb-8"
        >
          <label className="block text-xs font-mono text-zinc-500 uppercase tracking-widest mb-2">
            CT Number
          </label>
          <div className="relative">
            <input
              type="text"
              value={ctNumber}
              onChange={handleCtNumberChange}
              placeholder="Enter motherboard CT number"
              disabled={isAnalyzing}
              data-testid="ct-number-input"
              className={`w-full bg-[#18181B] border rounded-sm px-4 py-3 text-zinc-100 font-mono placeholder-zinc-600 focus:outline-none focus:ring-1 transition-all disabled:opacity-50 ${
                ctWarning 
                  ? "border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500" 
                  : "border-zinc-700 focus:border-cyan-500 focus:ring-cyan-500"
              }`}
            />
            {isCheckingCt && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          
          {/* Warning Banner */}
          {ctWarning && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm p-3"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-yellow-500 font-mono text-sm font-bold">
                    {ctWarning.message}
                  </div>
                  <div className="text-zinc-400 text-xs mt-1">
                    Last result: <span className={ctWarning.previousResult === "PASS" ? "text-emerald-500" : "text-red-500"}>{ctWarning.previousResult}</span> on {ctWarning.previousDate}
                  </div>
                  <div className="text-zinc-500 text-xs mt-1">
                    New analysis will be added to history
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Scan Mode Toggle */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.17 }}
          className="max-w-md mx-auto mb-8"
        >
          <label className="block text-xs font-mono text-zinc-500 uppercase tracking-widest mb-2">
            Scan Mode
          </label>
          <div className="flex gap-3">
            <button
              onClick={() => setDetailedMode(false)}
              disabled={isAnalyzing}
              className={`flex-1 px-4 py-3 rounded-sm font-mono text-sm transition-all ${
                !detailedMode 
                  ? "bg-cyan-500 text-black font-bold" 
                  : "bg-[#18181B] border border-zinc-700 text-zinc-400 hover:border-zinc-600"
              }`}
            >
              Fast (2×2)
              <div className="text-xs opacity-70 mt-1">~30-40 sec</div>
            </button>
            <button
              onClick={() => setDetailedMode(true)}
              disabled={isAnalyzing}
              className={`flex-1 px-4 py-3 rounded-sm font-mono text-sm transition-all ${
                detailedMode 
                  ? "bg-cyan-500 text-black font-bold" 
                  : "bg-[#18181B] border border-zinc-700 text-zinc-400 hover:border-zinc-600"
              }`}
            >
              Detailed (3×3)
              <div className="text-xs opacity-70 mt-1">~60-90 sec</div>
            </button>
          </div>
          {detailedMode && (
            <div className="mt-2 text-xs text-yellow-500">
              ⚠ Detailed mode may timeout on slow connections
            </div>
          )}
        </motion.div>

        {/* Upload Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid md:grid-cols-2 gap-6 mb-12"
        >
          <UploadZone
            label="FRONT SIDE"
            preview={frontPreview}
            onUpload={(file) => handleImageUpload(file, "front")}
            onClear={() => {
              setFrontImage(null);
              setFrontPreview(null);
            }}
            isAnalyzing={isAnalyzing}
            data-testid="upload-zone-front"
          />
          
          <UploadZone
            label="BACK SIDE"
            preview={backPreview}
            onUpload={(file) => handleImageUpload(file, "back")}
            onClear={() => {
              setBackImage(null);
              setBackPreview(null);
            }}
            isAnalyzing={isAnalyzing}
            data-testid="upload-zone-back"
          />
        </motion.div>

        {/* Action Buttons */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            onClick={handleAnalyze}
            disabled={!frontImage || !backImage || isAnalyzing}
            data-testid="analyze-button"
            className="bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-sm px-8 py-6 transition-all uppercase tracking-wide text-sm btn-glow disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
          >
            {isAnalyzing ? (
              <>
                <Scan className="w-5 h-5 mr-2 animate-pulse" />
                {analysisProgress || (detailedMode ? "Detailed Scan (3×3)..." : "Fast Scan (2×2)...")}
              </>
            ) : (
              <>
                <Scan className="w-5 h-5 mr-2" />
                Analyze Motherboard
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
          
          {(frontImage || backImage) && (
            <Button
              onClick={clearImages}
              variant="outline"
              data-testid="clear-button"
              className="bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 rounded-sm px-6 py-6 transition-all uppercase tracking-wide text-sm"
            >
              Clear All
            </Button>
          )}
        </motion.div>

        {/* Features */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="grid md:grid-cols-3 gap-6 mt-20"
        >
          {[
            {
              icon: Cpu,
              title: "Deep Scan Analysis",
              description: "3x3 grid inspection - like a magnifying glass scanning every section of the board"
            },
            {
              icon: Scan,
              title: "Visual Highlighting",
              description: "Precise highlighting of corrosion areas with location mapping and severity levels"
            },
            {
              icon: CircuitBoard,
              title: "High Accuracy",
              description: "Multi-pass analysis catches even subtle corrosion that overview scans miss"
            }
          ].map((feature, index) => (
            <div 
              key={index}
              className="bg-[#18181B] border border-zinc-800 rounded-sm p-6 interactive-card"
            >
              <feature.icon className="w-8 h-8 text-cyan-400 mb-4" />
              <h3 className="font-mono font-bold text-zinc-50 uppercase tracking-wide mb-2">
                {feature.title}
              </h3>
              <p className="text-zinc-400 text-sm">
                {feature.description}
              </p>
            </div>
          ))}
        </motion.div>

        {/* Footer */}
        <footer className="mt-20 text-center text-zinc-600 text-xs uppercase tracking-widest">
          <p>Upload front and back images of motherboard for comprehensive analysis</p>
        </footer>
      </div>
    </div>
  );
}
